import { AnimatePresence, motion } from "framer-motion";
import { ShieldAlert, X } from "lucide-react";
import { useEffect, useId, useRef } from "react";
import { useTheme } from "../ThemeContext";

interface PiracyStatementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PiracyStatementModal({
  isOpen,
  onClose,
}: PiracyStatementModalProps) {
  const { isDark } = useTheme();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const focusTimer = window.setTimeout(() => closeButtonRef.current?.focus(), 0);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.clearTimeout(focusTimer);
      previousFocusRef.current?.focus();
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto p-4 sm:items-center sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.section
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            className={`relative z-10 my-auto flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border shadow-2xl sm:max-h-[calc(100vh-3rem)] ${
              isDark
                ? "border-blue-900/60 bg-gray-900 text-white"
                : "border-blue-100 bg-white text-gray-900"
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className={`sticky top-0 z-10 border-b ${
                isDark ? "border-gray-800 bg-gray-900" : "border-blue-100 bg-white"
              }`}
            >
              <div className="h-1 w-full bg-gradient-to-r from-blue-600 via-sky-500 to-amber-500" />

              <div className="flex items-start justify-between gap-4 px-6 pb-4 pt-6">
                <div className="flex items-start gap-3">
                  <div
                    className={`mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
                      isDark ? "bg-blue-950 text-blue-200" : "bg-blue-50 text-blue-700"
                    }`}
                  >
                    <ShieldAlert className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <p
                      className={`text-xs font-semibold uppercase tracking-[0.18em] ${
                        isDark ? "text-blue-200" : "text-blue-700"
                      }`}
                    >
                      Legal Use Only
                    </p>
                    <h2 id={titleId} className="mt-1 text-2xl font-semibold">
                      Personal use and anti-piracy statement
                    </h2>
                  </div>
                </div>

                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={onClose}
                  className={`inline-flex h-11 w-11 items-center justify-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isDark
                      ? "border-gray-700 bg-gray-800 text-gray-200 hover:bg-gray-700 focus:ring-offset-gray-900"
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 focus:ring-offset-white"
                  }`}
                  aria-label="Close piracy statement"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div
              id={descriptionId}
              className={`flex-1 space-y-4 overflow-y-auto px-6 pb-6 pt-5 text-sm leading-7 sm:text-base ${
                isDark ? "text-gray-200" : "text-gray-700"
              }`}
            >
              <p>
                This site is not affiliated with Audible. It is only intended for lawful,
                personal use with audiobook files tied to your own Audible account.
              </p>

              <div
                className={`rounded-2xl border p-4 ${
                  isDark ? "border-gray-800 bg-gray-950/60" : "border-blue-100 bg-blue-50/70"
                }`}
              >
                <p className="font-semibold text-inherit">Allowed use</p>
                <ul className="mt-2 space-y-2 pl-5 marker:text-blue-500">
                  <li>Only use this site with books you personally bought on an Audible website.</li>
                  <li>Only use converted files for your own private listening and personal backups.</li>
                  <li>Only proceed if you are allowed to access and use the source file under applicable law and your Audible terms.</li>
                </ul>
              </div>

              <div
                className={`rounded-2xl border p-4 ${
                  isDark ? "border-amber-900/50 bg-amber-950/30" : "border-amber-200 bg-amber-50"
                }`}
              >
                <p className="font-semibold text-inherit">Strictly prohibited</p>
                <ul className="mt-2 space-y-2 pl-5 marker:text-amber-500">
                  <li>Using this site for any audiobook you did not buy yourself.</li>
                  <li>Sharing, uploading, distributing, selling, or posting converted files for anyone else.</li>
                  <li>Using this site to support piracy, unauthorized copying, or any commercial redistribution.</li>
                </ul>
              </div>

              <p>
                If you cannot confirm that the audiobook was purchased by you from Audible,
                do not use this site with that file. Any piracy-related use is forbidden.
              </p>
            </div>
          </motion.section>
        </div>
      )}
    </AnimatePresence>
  );
}
